import * as z from 'zod';
import { RectangleSchema } from '../../graphics/common.ts';
import { AttributeNameSchema, BoneNameSchema, ConditionSchema } from '../../graphics/conditions.ts';
import { LayerImageOverrideSchema, LayerMirrorSchema, LayerNormalDataSchema, LayerPrioritySchema, LayerStateOverridesSchema } from '../../graphics/layers/common.ts';

export const GraphicsSourceAutoMeshTemplateSchema = z.object({
	name: z.string(),
	parts: z.object({
		id: z.string(),
		priority: LayerPrioritySchema,
		pointType: z.string().array().optional(),
		mirror: LayerMirrorSchema.optional(),
	}).array(),
});
export type GraphicsSourceAutoMeshTemplate = z.infer<typeof GraphicsSourceAutoMeshTemplateSchema>;

export const GraphicsSourceAutoMeshGraphicalLayerSchema = z.object({
	name: z.string(),
	colorizationKey: z.string().optional(),
	imageOverrides: LayerImageOverrideSchema.array().optional(),
	/** Overrides applied to this layer while generating an item preview. */
	previewOverrides: LayerStateOverridesSchema.optional(),
	/** If true, this layer is hidden in the editor preview while the preview cutter's "Hide face" option is enabled. */
	previewHideFace: z.boolean().optional(),
});
export type GraphicsSourceAutoMeshGraphicalLayer = z.infer<typeof GraphicsSourceAutoMeshGraphicalLayerSchema>;

export const GraphicsSourceAutoMeshLayerVariableSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('bone'),
		bone: BoneNameSchema,
		stops: z.number().int().array(),
	}),
	z.object({
		type: z.literal('typedModule'),
		module: z.string(),
	}),
	z.object({
		type: z.literal('attribute'),
		attribute: AttributeNameSchema,
	}),
	z.object({
		type: z.literal('view'),
	}),
	z.object({
		type: z.literal('armRotation'),
		side: z.enum(['left', 'right']),
	}),
	z.object({
		type: z.literal('armFingers'),
		side: z.enum(['left', 'right']),
	}),
	z.object({
		type: z.literal('legsState'),
	}),
	z.object({
		type: z.literal('blink'),
	}),
]);
export type GraphicsSourceAutoMeshLayerVariable = z.infer<typeof GraphicsSourceAutoMeshLayerVariableSchema>;

export const GraphicsSourceAutoMeshLayerSchema = RectangleSchema.extend({
	type: z.literal('autoMesh'),
	/** If configured, then this condition needs to be satisfied for this layer to display. */
	enableCond: ConditionSchema.optional(),
	name: z.string(),
	points: z.string(),
	automeshTemplate: z.string(),
	disabledTemplateParts: z.string().array().optional(),
	graphicalLayers: GraphicsSourceAutoMeshGraphicalLayerSchema.array(),
	variables: GraphicsSourceAutoMeshLayerVariableSchema.array(),

	normalMap: LayerNormalDataSchema.optional(),
	imageMap: z.partialRecord(z.string(), z.string().array()),
}).strict();
export type GraphicsSourceAutoMeshLayer = z.infer<typeof GraphicsSourceAutoMeshLayerSchema>;
